import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit3 } from "react-icons/fi";
import {
  Currency,
  SourcingEventStatus,
  useGetFrameworkAgreementQuery,
  useUpdateSourcingEventValueMutation,
  type FrameworkAgreementDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { parseDecimalInput } from "../../../common/input/parseDecimalInput";
import { displaySum, formatMoney } from "../../../common/formatting/displaySum";
import { remainingUnderCeiling } from "../../framework-agreement/callOffAvailability";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import { CeilingRefusal, ceilingRefusalOf } from "./ceilingRefusal";

const EDITABLE_STATUSES = [
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
];

export const EventValueSection = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [updateValue, { isLoading }] = useUpdateSourcingEventValueMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const [refusal, setRefusal] = useState<CeilingRefusal | null>(null);
  const callOffFramework = useCallOffFramework(event);
  const remainingForThisValue = remainingForThisValueOf(event, callOffFramework.framework);
  const frameworkCurrency = callOffFramework.framework
    ? (callOffFramework.framework.ceilingValue?.currency ?? Currency.Nok)
    : callOffFramework.undisclosedCurrency;
  const awaitsFramework = callOffFramework.applies && frameworkCurrency === null;

  const currency = event.eventValue?.currency ?? Currency.Nok;
  const canManage = useCanManageSourcingEvent(event);
  const canEdit = canManage && EDITABLE_STATUSES.includes(event.status);
  const parsedDraft = parseDecimalInput(draftValue, { allowSpaceGrouping: true });
  const negativeDraft = parsedDraft.kind === "number" && parsedDraft.value < 0;
  const draftUnsavable = parsedDraft.kind === "notANumber" || negativeDraft;

  const save = async () => {
    if (draftUnsavable) return;
    try {
      await updateValue({
        eventId: event.id,
        updateSourcingEventValueRequest: {
          eventValue:
            parsedDraft.kind === "number" ? { value: parsedDraft.value, currency: frameworkCurrency ?? currency } : null,
        },
      }).unwrap();
      setIsEditing(false);
    } catch (e) {
      const ceilingRefusal = ceilingRefusalOf(e);
      if (!ceilingRefusal) {
        trigger(e);
        return;
      }
      if (callOffFramework.applies && !callOffFramework.undisclosedCurrency) await callOffFramework.refetch();
      setRefusal(ceilingRefusal);
    }
  };

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
      <Modal isOpen={isEditing} onClose={() => setIsEditing(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Edit the procurement value")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={draftUnsavable || refusal !== null}>
              <FormLabel>{t("Procurement value")}</FormLabel>
              <Input
                inputMode="decimal"
                autoComplete="off"
                value={draftValue}
                onChange={(e) => {
                  setDraftValue(e.target.value);
                  setRefusal(null);
                }}
              />
              <FormHelperText>{t("The value used in reporting, in NOK")}</FormHelperText>
              <FormErrorMessage>
                {refusal === "OTHER_CURRENCY"
                  ? t("The procurement value has to be in the framework agreement's currency, {{currency}}.", {
                      currency: frameworkCurrency ?? currency,
                    })
                  : refusal === "OVER_CEILING"
                  ? remainingForThisValue !== null
                    ? t("{{remaining}} is left under the ceiling value, and the procurement value has to fit under it.", {
                        remaining: displaySum({ value: Math.max(remainingForThisValue, 0), currency }),
                      })
                    : t("This value would take the framework agreement past its ceiling value.")
                  : refusal === "VALUE_REQUIRED"
                  ? t("Enter a procurement value. The framework agreement has a ceiling value, so the value is needed.")
                  : negativeDraft
                  ? t("The amount can't be negative")
                  : t("Write the amount as a number, for example 1 234,50")}
              </FormErrorMessage>
            </FormControl>
            {callOffFramework.isError && (
              <Alert status="error" rounded="md" fontSize="sm" mt="4" alignItems="flex-start">
                <AlertIcon />
                <Stack spacing="2" align="flex-start">
                  <AlertDescription>
                    {t("The framework agreement could not be loaded, so the value can't be checked against its ceiling value.")}
                  </AlertDescription>
                  <Button size="sm" variant="outline" onClick={() => void callOffFramework.refetch()}>
                    {t("Try again")}
                  </Button>
                </Stack>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter gap="3">
            <Button variant="ghost" onClick={() => setIsEditing(false)}>
              {t("Abort")}
            </Button>
            <Button
              colorScheme="teal"
              onClick={save}
              isLoading={isLoading || (awaitsFramework && !callOffFramework.isError)}
              isDisabled={draftUnsavable || awaitsFramework}
            >
              {t("Save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <HStack justify="space-between" align="flex-start">
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="1">
            {t("Procurement value")}
          </Text>
          <Text fontSize="lg" fontWeight="semibold" color="smText">
            {event.eventValue?.value != null
              ? displaySum({ value: event.eventValue.value, currency })
              : "–"}
          </Text>
        </Box>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FiEdit3} />}
            onClick={() => {
              setDraftValue(event.eventValue?.value != null ? formatMoney(event.eventValue.value) : "");
              setRefusal(null);
              setIsEditing(true);
            }}
          >
            {t("Edit")}
          </Button>
        )}
      </HStack>
    </Box>
  );
};

const useCallOffFramework = (event: SourcingEventDto) => {
  const miniCompetition = event.eventContext.type === "MiniCompetition" ? event.eventContext : null;
  const undisclosed = miniCompetition !== null && event.frameworkAgreementLink?.mayOpen === false;
  const { data: framework, isError, refetch } = useGetFrameworkAgreementQuery(
    miniCompetition && !undisclosed ? { frameworkAgreementId: miniCompetition.frameworkAgreementId } : skipToken
  );
  return {
    applies: miniCompetition !== null,
    framework,
    undisclosedCurrency: undisclosed ? (event.frameworkAgreementLink?.ceiling?.currency ?? Currency.Nok) : null,
    isError: !framework && !undisclosed && isError,
    refetch,
  };
};

const remainingForThisValueOf = (
  event: SourcingEventDto,
  framework: FrameworkAgreementDto | undefined,
): number | null => {
  const miniCompetition = event.eventContext.type === "MiniCompetition" ? event.eventContext : null;
  const remaining = framework ? remainingUnderCeiling(framework) : null;
  if (remaining === null) return null;
  return remaining + (event.eventValue?.value ?? miniCompetition?.estimatedValue?.value ?? 0);
};
